import { useCallback, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { useAcceptRecommendation, useRecommendation } from "@companion-core/shared/app/Queries";
import { RequestRecommendationMedia } from "@companion-core/shared/app/Components/Requests/RequestRecommendationDetail/requestRecommendationDetail.model";
import { PopInProps } from "@companion-core/shared/app/Components/PopIn/popIn.model";
import { APIVendorAddress } from "@companion-core/shared/app/Interfaces/API";
import { trackPageRecommendationDetail } from "@companion-core/shared/app/tracking/requests";

interface UseRequestRecommendationDetailProps {
  requestId?: string;
  proposalId?: string;
  recommendationId?: string;
  onAcceptProposalSuccess: () => void;
}

export const useRequestRecommendationDetail = ({
  requestId,
  proposalId,
  recommendationId,
  onAcceptProposalSuccess,
}: UseRequestRecommendationDetailProps) => {
  const { t } = useTranslation();
  const [isPopInOpen, setIsPopInOpen] = useState(false);
  const [popInArgs, setPopInArgs] = useState<PopInProps | null>(null);
  const popInInitialArgs: PopInProps = useMemo(
    () => ({
      testID: "recommendation__acceptation-popin",
      primaryButton: {
        text: t("request.my-requests.recommendation.popin.primary-button.text"),
        onClick: () => {
          setIsPopInOpen(false);
        },
        onPress: () => {
          setIsPopInOpen(false);
        },
      },
      onClose: () => {
        setIsPopInOpen(false);
      },
    }),
    []
  );
  const {
    data: recommendation,
    isPending,
    isError,
  } = useRecommendation(requestId ?? "", proposalId ?? "", recommendationId ?? "");
  const { mutateAsync: acceptRecommendation, isPending: isAccepting } = useAcceptRecommendation();
  const images = useMemo(
    () => recommendation?.data?.result?.images as RequestRecommendationMedia[],
    [recommendation]
  );
  const status = useMemo(() => recommendation?.data?.result?.status as string, [recommendation]);
  const title = useMemo(() => recommendation?.data?.result?.title as string, [recommendation]);
  const price = useMemo(() => recommendation?.data?.result?.price as string, [recommendation]);
  const detail = useMemo(() => recommendation?.data?.result?.detail as string, [recommendation]);
  const vendorId = useMemo(
    () => recommendation?.data?.result?.vendor_id as string,
    [recommendation]
  );
  const beginningAddress = useMemo(
    () => recommendation?.data?.result?.beginning_address as APIVendorAddress,
    [recommendation]
  );
  const endingAddress = useMemo(
    () => recommendation?.data?.result?.ending_address as APIVendorAddress,
    [recommendation]
  );
  const sendRequest = useCallback(
    async (actionType: string) => {
      setPopInArgs({
        ...popInInitialArgs,
        icon: "JP-Circle-check",
        title: t(`request.my-requests.recommendation.popin.${actionType}.title`),
      });
      try {
        if (actionType === "acceptation") {
          await acceptRecommendation({
            requestId: requestId ?? "",
            proposalId: proposalId ?? "",
            recommendationId: recommendationId ?? "",
          });
          onAcceptProposalSuccess();
        }
      } catch {
        setPopInArgs({
          ...popInInitialArgs,
          icon: "JP-Circle-close",
          title: t(`request.my-requests.recommendation.popin.${actionType}-error.title`),
          content: t(`request.my-requests.recommendation.popin.${actionType}-error.text`),
        });
        setIsPopInOpen(true);
      }
    },
    [requestId, proposalId, recommendationId]
  );

  useEffect(() => {
    if (!isPending) {
      trackPageRecommendationDetail({
        status,
        requestId,
        recommendationId,
        vendorId,
      });
    }
  }, [isPending]);

  return {
    t,
    status,
    isPending,
    isError,
    isAccepting,
    sendRequest,
    isPopInOpen,
    popInArgs,
    images,
    title,
    price,
    detail,
    beginningAddress,
    endingAddress,
    vendorId,
  };
};
